import React, { useState } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Globe, User } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useViews } from '@/api/views/use-views';
import { View, CreateViewRequest, UpdateViewRequest } from '@/models/view';
import { usePermissions } from '@/hooks/usePermissions';
import { PageContainer, PageHeader } from '@/components/shared/layout';

const ViewsManagementPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { isAdmin } = usePermissions();
    const [activeModule, setActiveModule] = useState<'leads' | 'customers'>('leads');
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedView, setSelectedView] = useState<View | null>(null);

    const {
        views: leadsViews,
        isLoading: leadsLoading,
        createView: createLeadsView,
        updateView: updateLeadsView,
        deleteView: deleteLeadsView,
    } = useViews('leads');

    const {
        views: customersViews,
        isLoading: customersLoading,
        createView: createCustomersView,
        updateView: updateCustomersView,
        deleteView: deleteCustomersView,
    } = useViews('customers');

    const currentViews = activeModule === 'leads' ? leadsViews : customersViews;
    const isLoading = activeModule === 'leads' ? leadsLoading : customersLoading;
    const createView = activeModule === 'leads' ? createLeadsView : createCustomersView;
    const updateView = activeModule === 'leads' ? updateLeadsView : updateCustomersView;
    const deleteView = activeModule === 'leads' ? deleteLeadsView : deleteCustomersView;

    const handleCreateView = () => {
        setSelectedView(null);
        setIsCreateDialogOpen(true);
    };

    const handleEditView = (view: View) => {
        // Users can only edit their own views, admins can edit global views
        if (!view.is_global && view.user_id) {
            // This is a user-specific view - allow editing
            setSelectedView(view);
            setIsEditDialogOpen(true);
        } else if (view.is_global && isAdmin) {
            // This is a global view - only admins can edit
            setSelectedView(view);
            setIsEditDialogOpen(true);
        } else {
            enqueueSnackbar('You can only edit your own views', { variant: 'error' });
        }
    };

    const handleDeleteView = (view: View) => {
        // Users can only delete their own views, admins can delete global views
        if (!view.is_global && view.user_id) {
            setSelectedView(view);
            setIsDeleteDialogOpen(true);
        } else if (view.is_global && isAdmin) {
            setSelectedView(view);
            setIsDeleteDialogOpen(true);
        } else {
            enqueueSnackbar('You can only delete your own views', { variant: 'error' });
        }
    };

    const handleCreateSubmit = async (formData: CreateViewRequest) => {
        try {
            await createView.mutateAsync({
                ...formData,
                module: activeModule,
            });
            enqueueSnackbar('View created successfully', { variant: 'success' });
            setIsCreateDialogOpen(false);
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to create view', {
                variant: 'error',
            });
        }
    };

    const handleUpdateSubmit = async (formData: UpdateViewRequest) => {
        if (!selectedView) return;
        try {
            await updateView.mutateAsync({
                id: selectedView.id,
                ...formData,
            });
            enqueueSnackbar('View updated successfully', { variant: 'success' });
            setIsEditDialogOpen(false);
            setSelectedView(null);
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to update view', {
                variant: 'error',
            });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedView) return;
        try {
            await deleteView.mutateAsync(selectedView.id);
            enqueueSnackbar('View deleted successfully', { variant: 'success' });
            setIsDeleteDialogOpen(false);
            setSelectedView(null);
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to delete view', {
                variant: 'error',
            });
        }
    };

    // Separate views into global and user-specific
    const globalViews = currentViews.filter(v => v.is_global);
    const userViews = currentViews.filter(v => !v.is_global);

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Views"
                description="Manage views for Leads and Customers list pages"
                actions={
                    isAdmin && (
                        <Button onClick={handleCreateView}>
                            <Plus className="h-4 w-4" />
                            Create Global View
                        </Button>
                    )
                }
            />

            <Tabs
                value={activeModule}
                onValueChange={v => setActiveModule(v as 'leads' | 'customers')}
            >
                <TabsList>
                    <TabsTrigger value="leads">Leads Views</TabsTrigger>
                    <TabsTrigger value="customers">Customers Views</TabsTrigger>
                </TabsList>

                <TabsContent value="leads" className="space-y-6">
                    <ViewsList
                        globalViews={globalViews}
                        userViews={userViews}
                        isLoading={isLoading}
                        isAdmin={isAdmin}
                        onEdit={handleEditView}
                        onDelete={handleDeleteView}
                    />
                </TabsContent>

                <TabsContent value="customers" className="space-y-6">
                    <ViewsList
                        globalViews={globalViews}
                        userViews={userViews}
                        isLoading={isLoading}
                        isAdmin={isAdmin}
                        onEdit={handleEditView}
                        onDelete={handleDeleteView}
                    />
                </TabsContent>
            </Tabs>

            {/* Create View Dialog */}
            {isAdmin && (
                <CreateViewDialog
                    isOpen={isCreateDialogOpen}
                    onClose={() => setIsCreateDialogOpen(false)}
                    onSubmit={handleCreateSubmit}
                    module={activeModule}
                    isLoading={createView.isPending}
                />
            )}

            {/* Edit View Dialog */}
            <EditViewDialog
                isOpen={isEditDialogOpen}
                onClose={() => {
                    setIsEditDialogOpen(false);
                    setSelectedView(null);
                }}
                onSubmit={handleUpdateSubmit}
                view={selectedView}
                isLoading={updateView.isPending}
                isAdmin={isAdmin}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteViewDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setSelectedView(null);
                }}
                onConfirm={handleDeleteConfirm}
                view={selectedView}
                isLoading={deleteView.isPending}
            />
        </PageContainer>
    );
};

interface ViewsListProps {
    globalViews: View[];
    userViews: View[];
    isLoading: boolean;
    isAdmin: boolean;
    onEdit: (view: View) => void;
    onDelete: (view: View) => void;
}

const ViewsList: React.FC<ViewsListProps> = ({
    globalViews,
    userViews,
    isLoading,
    isAdmin,
    onEdit,
    onDelete,
}) => {
    if (isLoading) {
        return <div className="text-center py-8">Loading views...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Global Views Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5" />
                        Global Views
                    </CardTitle>
                    <CardDescription>
                        Views visible to all users. Only administrators can manage these.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {globalViews.length === 0 ? (
                        <p className="text-muted-foreground text-center py-4">No global views</p>
                    ) : (
                        <div className="space-y-2">
                            {globalViews.map(view => (
                                <ViewItem
                                    key={view.id}
                                    view={view}
                                    isAdmin={isAdmin}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* User Views Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        My Views
                    </CardTitle>
                    <CardDescription>Your personal views</CardDescription>
                </CardHeader>
                <CardContent>
                    {userViews.length === 0 ? (
                        <p className="text-muted-foreground text-center py-4">No personal views</p>
                    ) : (
                        <div className="space-y-2">
                            {userViews.map(view => (
                                <ViewItem
                                    key={view.id}
                                    view={view}
                                    isAdmin={isAdmin}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

interface ViewItemProps {
    view: View;
    isAdmin: boolean;
    onEdit: (view: View) => void;
    onDelete: (view: View) => void;
}

const ViewItem: React.FC<ViewItemProps> = ({ view, isAdmin, onEdit, onDelete }) => {
    const canEdit = !view.is_global || (view.is_global && isAdmin);
    const canDelete = !view.is_global || (view.is_global && isAdmin);

    return (
        <div className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/40">
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{view.name}</h3>
                    {view.is_default && (
                        <Badge variant="default" className="text-xs">
                            Default
                        </Badge>
                    )}
                    {view.is_global ? (
                        <Badge variant="secondary" className="text-xs">
                            <Globe className="h-3 w-3 me-1" />
                            Global
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-xs">
                            <User className="h-3 w-3 me-1" />
                            Personal
                        </Badge>
                    )}
                </div>
                {view.description && (
                    <p className="text-sm text-muted-foreground mt-1">{view.description}</p>
                )}
                <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Filters: {view.filters ? Object.keys(view.filters).length : 0}</span>
                    <span>Columns: {view.visible_columns?.length || 0}</span>
                    {view.sort && (
                        <span>
                            Sort: {view.sort.key} ({view.sort.direction})
                        </span>
                    )}
                </div>
            </div>
            <div className="flex gap-2">
                {canEdit && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(view)}
                        aria-label={`Edit ${view.name}`}
                        title={`Edit ${view.name}`}
                    >
                        <Edit className="h-4 w-4" />
                    </Button>
                )}
                {canDelete && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(view)}
                        aria-label={`Delete ${view.name}`}
                        title={`Delete ${view.name}`}
                    >
                        <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                )}
            </div>
        </div>
    );
};

interface CreateViewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CreateViewRequest) => void;
    module: 'leads' | 'customers';
    isLoading: boolean;
}

const CreateViewDialog: React.FC<CreateViewDialogProps> = ({
    isOpen,
    onClose,
    onSubmit,
    module,
    isLoading,
}) => {
    const [formData, setFormData] = useState<CreateViewRequest>({
        module,
        name: '',
        description: '',
        is_global: true,
        filters: {},
        visible_columns: [],
        order: 0,
        is_default: false,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Create Global View</DialogTitle>
                    <DialogDescription>
                        Create a new global view for {module} that will be visible to all users.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            value={formData.description}
                            onChange={e =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <Label htmlFor="order">Display Order</Label>
                        <Input
                            id="order"
                            type="number"
                            value={formData.order}
                            onChange={e =>
                                setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                            }
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? 'Creating...' : 'Create View'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

interface EditViewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: UpdateViewRequest) => void;
    view: View | null;
    isLoading: boolean;
    isAdmin: boolean;
}

const EditViewDialog: React.FC<EditViewDialogProps> = ({
    isOpen,
    onClose,
    onSubmit,
    view,
    isLoading,
    isAdmin,
}) => {
    const [formData, setFormData] = useState<UpdateViewRequest>({
        name: '',
        description: '',
        order: 0,
        is_default: false,
    });

    React.useEffect(() => {
        if (view) {
            setFormData({
                name: view.name,
                description: view.description || '',
                order: view.order,
                is_default: view.is_default,
            });
        }
    }, [view]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!view) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Edit View</DialogTitle>
                    <DialogDescription>
                        {view.is_global
                            ? 'Edit global view (visible to all users)'
                            : 'Edit your personal view'}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="edit-name">Name *</Label>
                        <Input
                            id="edit-name"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="edit-description">Description</Label>
                        <Textarea
                            id="edit-description"
                            value={formData.description}
                            onChange={e =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <Label htmlFor="edit-order">Display Order</Label>
                        <Input
                            id="edit-order"
                            type="number"
                            value={formData.order}
                            onChange={e =>
                                setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                            }
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="edit-is-default"
                            checked={formData.is_default}
                            onChange={e =>
                                setFormData({ ...formData, is_default: e.target.checked })
                            }
                        />
                        <Label htmlFor="edit-is-default">Set as default view</Label>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? 'Updating...' : 'Update View'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

interface DeleteViewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    view: View | null;
    isLoading: boolean;
}

const DeleteViewDialog: React.FC<DeleteViewDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    view,
    isLoading,
}) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete View</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete "{view?.name}"? This action cannot be
                        undone.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ViewsManagementPage;
